import {
  useContext,
  useReducer,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  useEffect
} from 'react';
import type {
  FunctionComponent,
  CSSProperties,
  TransitionEvent,
  Context,
  PropsWithoutRef,
  Ref,
  Dispatch
} from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import Button from '../Button';
import Icon, { registerIcon } from '../Icon';
import Link, { StyledLink } from '../Link';
import * as timesIcon from '../Icon/icons/times.icon';
import {
  createUID,
  windowIsAvailable,
  withTestIds,
  hasProp,
  getActiveElement,
  getFocusables
} from '../../utils';
import {
  useConfiguration,
  useI18n,
  useLiveLog,
  useOS,
  useShortcut,
  useTestIds,
  useConsolidatedRef,
  useEscape,
  useFocusWithin
} from '../../hooks';
import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import '../../init';
import { registerAction } from '../ShortcutManager';
import { Keyboard } from '../Badges';
import { StyledKeyboard } from '../Badges/Keyboard';

import type { ToastMessage, ToasterProps, BaseToastMessage, CreatedMessage } from './Toaster.types';
import { ToasterContext } from './Context';
import type { ToasterContextValue } from './Context';
import { getToasterTestIds } from './Toaster.test-ids';

registerIcon(timesIcon);
registerAction(
  { id: 'JumpToToast', defaultKeyBinding: 'Alt+Enter' },
  { id: 'DismissToast', defaultKeyBinding: 'Alt+Q' }
);

type ToastState = (BaseToastMessage | CreatedMessage) & {
  id: NonNullable<ToastMessage['id']>;
  dismissAfter?: number;
};

type ToastReducerAction =
  | { type: 'push'; payload: ToastState }
  | { type: 'unmount'; payload: ToastState['id'] };

type ToastProps = ToastState & {
  // determines if this is the first toast in the list, used to dismiss the first toast with a shortcut, not exposed outside
  firstToast: boolean;
  topLevelDismiss: ToasterProps['onDismiss'];
  translateY: number;
  dispatch: Dispatch<ToastReducerAction>;
  ref: Ref<HTMLDivElement>;
};

declare module '../../init' {
  export interface CosmosGlobals {
    toasterContext?: Context<ToasterContextValue>;
  }
}

const StyledToastButton = styled.button(
  ({ theme }) => css`
    border: none;
    color: inherit;
    padding: 0;
    font-size: 1rem;
    margin-block: calc(${theme.base.spacing} / 2);
    margin-inline-end: calc(${theme.base.spacing} / 2);

    &:enabled:hover {
      background: #ffffff19;
    }

    &:enabled:focus {
      box-shadow: ${theme.base.shadow.focus};
    }
  `
);

StyledToastButton.defaultProps = defaultThemeProp;

const StyledToastContent = styled.div(({ theme }) => {
  return css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: clip;
    text-overflow: ellipsis;
    font-size: ${theme.base['font-size']};
    margin-inline-start: calc(${theme.base['border-radius']} / 2);
    padding-inline-start: calc(${theme.base.spacing} / 2);

    ${StyledLink} {
      color: ${theme.base.colors.blue.light};
    }
  `;
});

StyledToastContent.defaultProps = defaultThemeProp;

const StyledToast = styled.div(({ theme }) => {
  return css`
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: 0;
    width: 100%;
    background: ${theme.base.colors.slate['extra-dark']};
    color: ${theme.base.colors.white};
    border-radius: calc(${theme.base['border-radius']} / 2);
    opacity: var(--opacity);
    transform: translateY(var(--translateY)) scale(var(--scale));
    transition-property: transform, opacity;
    transition-duration: ${theme.base.animation.speed};
    transition-timing-function: ${theme.base.animation.timing.ease};
    font-weight: ${theme.base['font-weight']['semi-bold']};

    :focus {
      box-shadow: ${theme.base.shadow.focus};
    }

    ${StyledKeyboard} {
      color: ${theme.base.palette.light};
      background-color: ${theme.base.colors.gray.dark};
      border-color: ${theme.base.colors.gray.medium};
      inset-block-start: unset;
    }
  `;
});

StyledToast.defaultProps = defaultThemeProp;

const StyledToaster = styled.div(({ theme }) => {
  return css`
    position: fixed;
    z-index: ${theme.base['z-index'].toast};
    width: min(40ch, calc(100% - 1rem));
    inset-inline-start: 50%;
    transform: translateX(-50%);
    top: calc(100dvh - var(--appshell-bottom-offset, 0rem));
  `;
});

StyledToaster.defaultProps = defaultThemeProp;

function reducer(current: ToastState[], action: ToastReducerAction) {
  switch (action.type) {
    case 'push':
      return [
        ...current,
        {
          ...action.payload
        }
      ];

    case 'unmount':
      return current.filter(m => m.id !== action.payload);

    default:
      return current;
  }
}

const Toast = forwardRef(function Toast(
  {
    id,
    firstToast,
    dispatch,
    topLevelDismiss,
    translateY,
    onDismiss,
    dismissAfter,
    ...restProps
  }: PropsWithoutRef<ToastProps>,
  ref: ToastProps['ref']
) {
  const [dismissed, setDismissed] = useState(false);
  const timeoutRef = useRef(NaN);
  const t = useI18n();
  const { macintosh } = useOS();
  const { announcePolite } = useLiveLog();
  const content = hasProp(restProps, 'href') ? t('created') : restProps.content;
  const initiatorRef = useRef<HTMLElement | null>(null);
  const containerRef = useConsolidatedRef(ref);

  useEffect(() => {
    let fullMessage = content;
    if (hasProp(restProps, 'href')) {
      // CreatedMessage
      fullMessage = restProps.caseId
        ? `${content} ${restProps.caseId} ${restProps.label}`
        : `${content} ${restProps.label}`;
    }

    const shortcutJumpMessage = t('keypress_instruction', [
      macintosh ? 'Option + Enter' : 'Alt + Enter',
      t('shortcut_JumpToToast')
    ]);

    const shortcutDismissMessage = t('keypress_instruction', [
      macintosh ? 'Option + Q' : 'Alt + Q',
      t('shortcut_DismissToast')
    ]);

    announcePolite({
      message: `${fullMessage}. ${shortcutJumpMessage}. ${shortcutDismissMessage}.`,
      type: 'acknowledgement'
    });
  }, []);

  const onTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (dismissed && e.propertyName === 'opacity' && e.target === e.currentTarget) {
      dispatch({ type: 'unmount', payload: id });
    }
  };

  const dismiss = (timeout: boolean) => () => {
    if (initiatorRef.current) {
      initiatorRef.current?.focus?.();
      initiatorRef.current = null;
    }
    clearTimeout(timeoutRef.current);
    if (dismissed) return;
    setDismissed(true);
    onDismiss?.({ id, timeout });
    topLevelDismiss?.({ id, timeout });
  };

  const startTimer = () => {
    if (dismissAfter) {
      timeoutRef.current = window.setTimeout(dismiss(true), dismissAfter);
    }
  };

  useEffect(() => {
    startTimer();
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  // Alt+Enter focuses on the first focusable element in the first toast.
  // Tab to the next toast if there are multiple.
  useShortcut('JumpToToast', () => {
    const activeElement = getActiveElement();
    if (activeElement && activeElement !== document.body) {
      initiatorRef.current = activeElement as HTMLElement;
    }
    if (firstToast && containerRef.current) {
      getFocusables(containerRef.current)[0]?.focus();
    }
  });

  // Alt+Q dismisses the first toast.
  useShortcut('DismissToast', () => {
    if (firstToast) dismiss(false)();
  });

  // Escape dismisses the toast and focuses the initiator.
  useEscape(() => {
    const currentFocus = getActiveElement();
    if (containerRef.current?.contains(currentFocus)) {
      dismiss(false)();
    }
  });

  useFocusWithin([containerRef], (focused: boolean) => {
    if (focused) {
      window.clearTimeout(timeoutRef.current);
    } else {
      startTimer();
    }
  });

  return (
    <Flex
      {...restProps}
      data-app-region
      container={{ alignItems: 'center', gap: 0.25 }}
      as={StyledToast}
      ref={containerRef}
      style={
        {
          '--translateY': `${-translateY}px`,
          '--opacity': dismissed ? '0' : '1',
          '--scale': dismissed ? '0' : '1'
        } as CSSProperties
      }
      onTransitionEnd={onTransitionEnd}
    >
      <Flex item={{ grow: 1 }} as={StyledToastContent}>
        {content}
        {hasProp(restProps, 'href') ? (
          <>
            &nbsp;
            <Link href={restProps.href}>
              {restProps.caseId ? `(${restProps.caseId}): ${restProps.label}` : restProps.label}
            </Link>
          </>
        ) : undefined}
      </Flex>
      <Flex container={{ inline: true, justify: 'end', gap: 0.5 }}>
        <Keyboard keyName='Alt' />
        <Keyboard keyName='Enter' />
      </Flex>
      <Button
        as={StyledToastButton}
        variant='simple'
        onClick={dismiss(false)}
        icon
        aria-label={t('dismiss_label')}
      >
        <Icon name='times' />
      </Button>
    </Flex>
  );
});

const Toaster: FunctionComponent<ToasterProps> = ({
  testId,
  children,
  onDismiss
}: ToasterProps) => {
  const { portalTarget } = useConfiguration();
  const [messages, dispatch] = useReducer(reducer, []);
  const [yOffsets, setYOffsets] = useState<number[]>([]);
  const heightHashRef = useRef<{
    [id: string]: number;
  }>({});
  const testIds = useTestIds(testId, getToasterTestIds);

  // This effect will cause a re-render with a translateY value set for each toast.
  useLayoutEffect(() => {
    const heights = [...messages].reverse().map(({ id }) => heightHashRef.current[id]);

    setYOffsets(
      heights
        .map((_, i) => heights.slice(0, i + 1).reduce((sum, cur) => sum + cur + 4, 0))
        .reverse()
    );
  }, [messages.length]);

  const providerValue = useMemo(
    () => ({
      push(incoming: ToastMessage) {
        const message: ToastState = {
          ...incoming,
          id: incoming.id ?? createUID(),
          dismissAfter: hasProp(incoming, 'href') ? 15_000 : 7_500
        };

        dispatch({
          type: 'push',
          payload: message
        });
      },
      initialized: true
    }),
    []
  );

  // Don't create additional ToasterContext.Providers.
  const context = windowIsAvailable
    ? window.cosmos.toasterContext ?? ToasterContext
    : ToasterContext;
  const priorCtx = useContext(context);
  if (priorCtx.initialized) {
    return <context.Provider value={priorCtx}>{children}</context.Provider>;
  }

  return (
    <>
      <context.Provider value={providerValue}>{children}</context.Provider>
      {!!messages.length &&
        portalTarget &&
        createPortal(
          <StyledToaster data-testid={testIds.root}>
            {messages.map((message, i) => {
              const sharedProps = {
                translateY: yOffsets[i] ?? 0,
                firstToast: i === 0,
                topLevelDismiss: onDismiss,
                dispatch,
                ref: el => {
                  if (el) {
                    heightHashRef.current[message.id] = el.offsetHeight;
                  } else {
                    delete heightHashRef.current[message.id];
                  }
                }
              } satisfies Partial<ToastProps>;

              if (hasProp(message, 'href')) {
                return (
                  // Toast with Link
                  <Toast
                    data-testid={testIds.toast}
                    {...message}
                    {...sharedProps}
                    key={message.id}
                  />
                );
              }

              return (
                // Base Toast
                <Toast data-testid={testIds.toast} {...message} {...sharedProps} key={message.id} />
              );
            })}
          </StyledToaster>,
          portalTarget
        )}
    </>
  );
};

export default withTestIds(Toaster, getToasterTestIds);
